/**
 * StockDetail.tsx - Individual Stock Detail Page
 *
 * Displays comprehensive information about a single stock including:
 * - Company overview
 * - Price and market data
 * - Valuation metrics
 * - Profitability ratios
 * - Technical indicators
 * - Ownership and insider data
 */

import { useState, useEffect, ReactElement } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  TrendingUp,
  DollarSign,
  BarChart3,
  Activity,
  Users,
  Target,
  Globe,
  Calendar,
  Briefcase,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  Minus,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { stocksAPI, StockDetailResponse, StockPeer } from '../../services/api';
import BookmarkButton from '../../components/BookmarkButton';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatNumber = (value: number | undefined | null, decimals: number = 2): string => {
  if (value === null || value === undefined) return '--';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatCurrency = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '--';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatLargeNumber = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '--';
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (absValue >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number | undefined | null, decimals: number = 2): string => {
  if (value === null || value === undefined) return '--';
  return `${(value * 100).toFixed(decimals)}%`;
};

const formatPercentRaw = (value: number | undefined | null, decimals: number = 2): string => {
  if (value === null || value === undefined) return '--';
  return `${value.toFixed(decimals)}%`;
};

const formatRatio = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '--';
  return value.toFixed(2);
};

const formatVolume = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return '--';
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return value.toLocaleString();
};

const getChangeColor = (value: number | undefined | null): string => {
  if (value === null || value === undefined) return 'text-slate-400';
  if (value > 0) return 'text-emerald-400';
  if (value < 0) return 'text-red-400';
  return 'text-slate-400';
};

const getChangeIcon = (value: number | undefined | null): ReactElement => {
  if (value === null || value === undefined) return <Minus className="w-4 h-4" />;
  if (value > 0) return <ChevronUp className="w-4 h-4" />;
  if (value < 0) return <ChevronDown className="w-4 h-4" />;
  return <Minus className="w-4 h-4" />;
};

// ============================================================================
// COMPONENTS
// ============================================================================

interface MetricCardProps {
  label: string;
  value: string;
  subValue?: string;
  isGood?: boolean | null;
}

function MetricCard({ label, value, subValue, isGood }: MetricCardProps): ReactElement {
  let valueColor = 'text-slate-200';
  if (isGood === true) valueColor = 'text-emerald-400';
  if (isGood === false) valueColor = 'text-red-400';

  return (
    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-mono font-semibold ${valueColor}`}>{value}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: ReactElement;
  children: React.ReactNode;
}

function Section({ title, icon, children }: SectionProps): ReactElement {
  return (
    <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-emerald-400">{icon}</span>
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

interface PeerRowProps {
  peer: StockPeer;
}

function PeerRow({ peer }: PeerRowProps): ReactElement {
  return (
    <Link
      to={`/stocks/${peer.symbol}`}
      className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-800/50 transition-colors"
    >
      <div>
        <div className="font-mono text-sm text-slate-200">{peer.symbol}</div>
        <div className="text-xs text-slate-500 truncate max-w-[150px]">{peer.name}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm text-slate-300">{formatCurrency(peer.price)}</div>
        <div className={`text-xs font-mono ${getChangeColor(peer.price_change_pct)}`}>
          {formatPercentRaw(peer.price_change_pct)}
        </div>
      </div>
    </Link>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function StockDetail(): ReactElement {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();

  const [stock, setStock] = useState<StockDetailResponse | null>(null);
  const [peers, setPeers] = useState<StockPeer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!symbol) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [detailRes, peersRes] = await Promise.all([
          stocksAPI.getStockDetail(symbol),
          stocksAPI.getStockPeers(symbol, 8),
        ]);
        setStock(detailRes.data);
        setPeers(peersRes.data.peers);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load stock data';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading {symbol}...</span>
        </div>
      </div>
    );
  }

  if (error || !stock) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl text-slate-200 mb-2">Failed to load stock</h2>
          <p className="text-slate-400 mb-4">{error || 'Stock not found'}</p>
          <button
            onClick={() => navigate('/stocks')}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors"
          >
            Back to Stocks
          </button>
        </div>
      </div>
    );
  }

  const { company, price, valuation, profitability, leverage, dividends, technicals, returns, ownership, insiders, analysts } = stock;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-slate-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-100 font-mono">{stock.symbol}</h1>
                  {company.exchange && (
                    <span className="px-2 py-0.5 text-xs bg-slate-800 rounded text-slate-400">
                      {company.exchange}
                    </span>
                  )}
                  <BookmarkButton
                    itemType="stock"
                    itemId={stock.symbol}
                    title={stock.symbol}
                    subtitle={company.name}
                    url={`/stocks/${stock.symbol}`}
                    size="md"
                    className="text-slate-400 hover:text-amber-400 hover:bg-slate-800"
                  />
                </div>
                <p className="text-slate-400 text-sm">{company.name}</p>
              </div>
            </div>

            {/* Price Header */}
            <div className="text-right">
              <div className="text-3xl font-bold text-slate-100 font-mono">
                {formatCurrency(price.price)}
              </div>
              <div className={`flex items-center justify-end gap-1 ${getChangeColor(price.price_change_pct)}`}>
                {getChangeIcon(price.price_change_pct)}
                <span className="font-mono">
                  {formatCurrency(price.price_change)} ({formatPercentRaw(price.price_change_pct)})
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Overview */}
            <Section title="Company Overview" icon={<Building2 className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                <div>
                  <div className="text-xs text-slate-500">Sector</div>
                  <div className="text-sm text-slate-300">{company.sector || '--'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Industry</div>
                  <div className="text-sm text-slate-300">{company.industry || '--'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Country</div>
                  <div className="text-sm text-slate-300">{company.country || '--'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">IPO Year</div>
                  <div className="text-sm text-slate-300">{company.ipo_year || '--'}</div>
                </div>
              </div>
              {company.description && (
                <p className="text-sm text-slate-400 line-clamp-3">{company.description}</p>
              )}
              <div className="flex items-center gap-4 mt-4">
                {company.website && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
                  >
                    <Globe className="w-3 h-3" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {company.ceo && (
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Briefcase className="w-3 h-3" />
                    CEO: {company.ceo}
                  </div>
                )}
                {company.employees && (
                  <div className="flex items-center gap-1 text-xs text-slate-400">
                    <Users className="w-3 h-3" />
                    {company.employees.toLocaleString()} employees
                  </div>
                )}
              </div>
            </Section>

            {/* Market Data */}
            <Section title="Market Data" icon={<BarChart3 className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="Market Cap" value={formatLargeNumber(price.market_cap)} />
                <MetricCard label="Volume" value={formatVolume(price.volume)} />
                <MetricCard label="Avg Volume" value={formatVolume(price.avg_volume)} />
                <MetricCard label="52W High" value={formatCurrency(price.high_52w)} />
                <MetricCard label="52W Low" value={formatCurrency(price.low_52w)} />
                <MetricCard
                  label="% From High"
                  value={formatPercentRaw(price.pct_from_high)}
                  isGood={price.pct_from_high !== undefined && price.pct_from_high !== null ? price.pct_from_high > -10 : null}
                />
                <MetricCard
                  label="% From Low"
                  value={formatPercentRaw(price.pct_from_low)}
                  isGood={price.pct_from_low !== undefined && price.pct_from_low !== null ? price.pct_from_low > 0 : null}
                />
                <MetricCard label="52W Range %" value={formatPercentRaw(price.range_position_52w)} />
              </div>
            </Section>

            {/* Valuation */}
            <Section title="Valuation" icon={<Target className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="P/E (TTM)" value={formatRatio(valuation.pe_ratio_ttm)} />
                <MetricCard label="Forward P/E" value={formatRatio(valuation.forward_pe)} />
                <MetricCard label="PEG Ratio" value={formatRatio(valuation.peg_ratio)} />
                <MetricCard label="P/B Ratio" value={formatRatio(valuation.pb_ratio)} />
                <MetricCard label="P/S Ratio" value={formatRatio(valuation.ps_ratio)} />
                <MetricCard label="EV/EBITDA" value={formatRatio(valuation.ev_to_ebitda)} />
                <MetricCard label="EV/Sales" value={formatRatio(valuation.ev_to_sales)} />
                <MetricCard label="Enterprise Value" value={formatLargeNumber(valuation.enterprise_value)} />
                <MetricCard
                  label="Earnings Yield"
                  value={formatPercent(valuation.earnings_yield)}
                  isGood={valuation.earnings_yield !== undefined && valuation.earnings_yield !== null ? valuation.earnings_yield > 0.05 : null}
                />
                <MetricCard
                  label="FCF Yield"
                  value={formatPercent(valuation.fcf_yield)}
                  isGood={valuation.fcf_yield !== undefined && valuation.fcf_yield !== null ? valuation.fcf_yield > 0.05 : null}
                />
                <MetricCard label="Price/Fair Value" value={formatRatio(valuation.price_to_fair_value)} />
                <MetricCard label="Graham Number" value={formatCurrency(valuation.graham_number)} />
              </div>
            </Section>

            {/* Profitability */}
            <Section title="Profitability" icon={<TrendingUp className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard
                  label="ROE"
                  value={formatPercent(profitability.roe)}
                  isGood={profitability.roe !== undefined && profitability.roe !== null ? profitability.roe > 0.15 : null}
                />
                <MetricCard
                  label="ROA"
                  value={formatPercent(profitability.roa)}
                  isGood={profitability.roa !== undefined && profitability.roa !== null ? profitability.roa > 0.05 : null}
                />
                <MetricCard
                  label="ROIC"
                  value={formatPercent(profitability.roic)}
                  isGood={profitability.roic !== undefined && profitability.roic !== null ? profitability.roic > 0.12 : null}
                />
                <MetricCard label="ROCE" value={formatPercent(profitability.roce)} />
                <MetricCard label="Gross Margin" value={formatPercent(profitability.gross_margin)} />
                <MetricCard label="Operating Margin" value={formatPercent(profitability.operating_margin)} />
                <MetricCard
                  label="Net Margin"
                  value={formatPercent(profitability.net_profit_margin)}
                  isGood={profitability.net_profit_margin !== undefined && profitability.net_profit_margin !== null ? profitability.net_profit_margin > 0.1 : null}
                />
                <MetricCard label="EBITDA Margin" value={formatPercent(profitability.ebitda_margin)} />
              </div>
            </Section>

            {/* Financial Health */}
            <Section title="Financial Health" icon={<DollarSign className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <MetricCard
                  label="Debt/Equity"
                  value={formatRatio(leverage.debt_to_equity)}
                  isGood={leverage.debt_to_equity !== undefined && leverage.debt_to_equity !== null ? leverage.debt_to_equity < 1 : null}
                />
                <MetricCard label="Debt/Assets" value={formatRatio(leverage.debt_to_assets)} />
                <MetricCard label="Net Debt/EBITDA" value={formatRatio(leverage.net_debt_to_ebitda)} />
                <MetricCard
                  label="Current Ratio"
                  value={formatRatio(leverage.current_ratio)}
                  isGood={leverage.current_ratio !== undefined && leverage.current_ratio !== null ? leverage.current_ratio > 1.5 : null}
                />
                <MetricCard label="Quick Ratio" value={formatRatio(leverage.quick_ratio)} />
                <MetricCard
                  label="Interest Coverage"
                  value={formatRatio(leverage.interest_coverage)}
                  isGood={leverage.interest_coverage !== undefined && leverage.interest_coverage !== null ? leverage.interest_coverage > 3 : null}
                />
              </div>
            </Section>

            {/* Dividends */}
            {(dividends.dividend_yield || dividends.dividend_per_share) && (
              <Section title="Dividends" icon={<DollarSign className="w-4 h-4" />}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <MetricCard
                    label="Dividend Yield"
                    value={formatPercent(dividends.dividend_yield)}
                    isGood={dividends.dividend_yield !== undefined && dividends.dividend_yield !== null ? dividends.dividend_yield > 0.02 : null}
                  />
                  <MetricCard label="Dividend/Share" value={formatCurrency(dividends.dividend_per_share)} />
                  <MetricCard
                    label="Payout Ratio"
                    value={formatPercent(dividends.payout_ratio)}
                    isGood={dividends.payout_ratio !== undefined && dividends.payout_ratio !== null ? dividends.payout_ratio < 0.6 : null}
                  />
                  <MetricCard label="Last Dividend" value={formatCurrency(dividends.last_dividend)} />
                </div>
              </Section>
            )}

            {/* Technical Indicators */}
            <Section title="Technical Indicators" icon={<Activity className="w-4 h-4" />}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard label="RSI (14)" value={formatNumber(technicals.rsi_14, 1)} subValue={technicals.rsi_14 !== undefined && technicals.rsi_14 !== null ? (technicals.rsi_14 > 70 ? 'Overbought' : technicals.rsi_14 < 30 ? 'Oversold' : 'Neutral') : undefined} />
                <MetricCard label="SMA 20" value={formatCurrency(technicals.sma_20)} />
                <MetricCard label="SMA 50" value={formatCurrency(technicals.sma_50)} />
                <MetricCard label="SMA 200" value={formatCurrency(technicals.sma_200)} />
                <MetricCard label="vs SMA20" value={formatPercentRaw(technicals.price_vs_sma20)} isGood={technicals.price_vs_sma20 !== undefined && technicals.price_vs_sma20 !== null ? technicals.price_vs_sma20 > 0 : null} />
                <MetricCard label="vs SMA50" value={formatPercentRaw(technicals.price_vs_sma50)} isGood={technicals.price_vs_sma50 !== undefined && technicals.price_vs_sma50 !== null ? technicals.price_vs_sma50 > 0 : null} />
                <MetricCard label="vs SMA200" value={formatPercentRaw(technicals.price_vs_sma200)} isGood={technicals.price_vs_sma200 !== undefined && technicals.price_vs_sma200 !== null ? technicals.price_vs_sma200 > 0 : null} />
                <MetricCard label="Beta" value={formatRatio(technicals.beta)} />
                <MetricCard label="Volatility (20D)" value={formatPercentRaw(technicals.volatility_20d)} />
                <MetricCard label="Volatility (60D)" value={formatPercentRaw(technicals.volatility_60d)} />
                <MetricCard label="ATR (14)" value={formatCurrency(technicals.atr_14)} />
                <MetricCard
                  label="Trend Signal"
                  value={technicals.golden_cross ? 'Golden Cross' : technicals.death_cross ? 'Death Cross' : '--'}
                  isGood={technicals.golden_cross ? true : technicals.death_cross ? false : null}
                />
              </div>
            </Section>
          </div>

          {/* Right Column - Side Info */}
          <div className="space-y-6">
            {/* Returns Performance */}
            <Section title="Returns" icon={<TrendingUp className="w-4 h-4" />}>
              <div className="space-y-2">
                {[
                  { label: '1 Day', value: returns.return_1d },
                  { label: '1 Week', value: returns.return_1w },
                  { label: '1 Month', value: returns.return_1m },
                  { label: '3 Months', value: returns.return_3m },
                  { label: '6 Months', value: returns.return_6m },
                  { label: 'YTD', value: returns.return_ytd },
                  { label: '1 Year', value: returns.return_1y },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                    <span className="text-sm text-slate-400">{item.label}</span>
                    <span className={`font-mono text-sm ${getChangeColor(item.value)}`}>
                      {formatPercentRaw(item.value)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-700/50">
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">Max Drawdown YTD</span>
                  <span className="font-mono text-xs text-red-400">{formatPercentRaw(returns.max_drawdown_ytd)}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-xs text-slate-500">Max Drawdown 1Y</span>
                  <span className="font-mono text-xs text-red-400">{formatPercentRaw(returns.max_drawdown_1y)}</span>
                </div>
              </div>
            </Section>

            {/* Analyst Data */}
            <Section title="Analyst Targets" icon={<Target className="w-4 h-4" />}>
              <div className="space-y-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-slate-100 font-mono">
                    {formatCurrency(analysts.price_target_consensus)}
                  </div>
                  <div className="text-xs text-slate-500">Consensus Target</div>
                </div>
                <div className={`text-center py-2 rounded-lg ${analysts.price_target_upside !== undefined && analysts.price_target_upside !== null && analysts.price_target_upside > 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                  <div className={`text-lg font-mono ${getChangeColor(analysts.price_target_upside)}`}>
                    {formatPercentRaw(analysts.price_target_upside)} upside
                  </div>
                </div>
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Analysts (Month): {analysts.analyst_count_month ?? '--'}</span>
                  <span>Analysts (Quarter): {analysts.analyst_count_quarter ?? '--'}</span>
                </div>
              </div>
            </Section>

            {/* Ownership */}
            <Section title="Institutional Ownership" icon={<Users className="w-4 h-4" />}>
              <div className="space-y-2">
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400">Ownership %</span>
                  <span className="font-mono text-sm text-slate-200">{formatPercent(ownership.institutional_ownership_pct)}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400">Change</span>
                  <span className={`font-mono text-sm ${getChangeColor(ownership.institutional_ownership_change)}`}>
                    {formatPercent(ownership.institutional_ownership_change)}
                  </span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400"># Holders</span>
                  <span className="font-mono text-sm text-slate-200">{ownership.institutional_holders_count?.toLocaleString() ?? '--'}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-700/50">
                  <div className="text-center">
                    <div className="text-emerald-400 font-mono text-sm">{ownership.new_positions ?? '--'}</div>
                    <div className="text-xs text-slate-500">New</div>
                  </div>
                  <div className="text-center">
                    <div className="text-emerald-400 font-mono text-sm">{ownership.increased_positions ?? '--'}</div>
                    <div className="text-xs text-slate-500">Increased</div>
                  </div>
                  <div className="text-center">
                    <div className="text-red-400 font-mono text-sm">{ownership.closed_positions ?? '--'}</div>
                    <div className="text-xs text-slate-500">Closed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-red-400 font-mono text-sm">{ownership.reduced_positions ?? '--'}</div>
                    <div className="text-xs text-slate-500">Reduced</div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Insider Activity */}
            <Section title="Insider Activity" icon={<Briefcase className="w-4 h-4" />}>
              <div className="space-y-2">
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400">Buy/Sell Ratio</span>
                  <span className={`font-mono text-sm ${insiders.buy_sell_ratio !== undefined && insiders.buy_sell_ratio !== null && insiders.buy_sell_ratio > 1 ? 'text-emerald-400' : 'text-slate-200'}`}>
                    {formatRatio(insiders.buy_sell_ratio)}
                  </span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400">Total Purchases</span>
                  <span className="font-mono text-sm text-emerald-400">{formatLargeNumber(insiders.total_purchases)}</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-slate-400">Total Sales</span>
                  <span className="font-mono text-sm text-red-400">{formatLargeNumber(insiders.total_sales)}</span>
                </div>
                <div className="flex justify-between py-1.5 text-xs">
                  <span className="text-slate-500">Buy Transactions: {insiders.buy_transactions ?? '--'}</span>
                  <span className="text-slate-500">Sell Transactions: {insiders.sell_transactions ?? '--'}</span>
                </div>
              </div>
            </Section>

            {/* Peer Companies */}
            {peers.length > 0 && (
              <Section title="Peer Companies" icon={<Building2 className="w-4 h-4" />}>
                <div className="space-y-1">
                  {peers.map((peer) => (
                    <PeerRow key={peer.symbol} peer={peer} />
                  ))}
                </div>
              </Section>
            )}

            {/* Data Timestamp */}
            <div className="text-center text-xs text-slate-500">
              <Calendar className="w-3 h-3 inline mr-1" />
              Data as of: {stock.data_as_of || 'Unknown'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
